import { type ReactNode } from 'react';
import { Filter, X, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ActiveFilter {
  key: string;
  label: string;
  value: string;
  onClear: () => void;
}

interface Props {
  children: ReactNode;
  /** Lista de filtros ativos para exibir como chips. */
  active?: ActiveFilter[];
  /** Callback para "Limpar todos". Se omitido, esconde o botão. */
  onClearAll?: () => void;
  className?: string;
}

/**
 * Wrapper visual padrao para barra de filtros. Layout:
 *   [icone] [filtros - flex wrap] [chips ativos] [Limpar todos]
 * Cada pagina compoe os controles internos como quiser (Select, Input, etc).
 */
export function FilterBar({ children, active, onClearAll, className }: Props) {
  const hasActive = (active?.length ?? 0) > 0;
  return (
    <Card className={cn('mb-4 p-3', className)}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {children}
          {hasActive && onClearAll && (
            <Button variant="ghost" size="sm" onClick={onClearAll} className="ml-auto">
              <RotateCcw className="h-3 w-3 mr-1" /> Limpar filtros
            </Button>
          )}
        </div>
        {hasActive && (
          <div className="flex flex-wrap gap-1 border-t pt-2">
            <span className="text-xs text-muted-foreground mr-1 self-center">Ativos:</span>
            {active!.map(f => (
              <Badge key={f.key} variant="secondary" className="gap-1 pl-2 pr-1 py-0.5">
                <span className="text-[10px] uppercase opacity-70">{f.label}</span>
                <span className="text-xs">{f.value}</span>
                <button
                  type="button"
                  onClick={f.onClear}
                  className="ml-1 hover:bg-foreground/10 rounded p-0.5"
                  title={`Remover ${f.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
